import React from "react";
import { Text } from "@chakra-ui/react";

import { useStoreTags } from "@hooks";
import { getPartTypeFromSerialNumber, Module, StepFrontend } from "@constants";

import { JsonForm, JsonFormControl } from "../JsonForm";
import { ButtonModal } from "../ModalWrappers";
import { ModuleLocationInTransit } from "@constants";

function download(filename: string, text: string) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    "data:text/plain;charset=utf-8," + encodeURIComponent(text),
  );
  element.setAttribute("download", filename);

  element.style.display = "none";
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

type AddDownloadDataModalProps = {
  dbModule: Module;
  steps: StepFrontend[];
};

function makeDownloadDataControl() {
  return [] as JsonFormControl[];
}

export function AddDownloadDataModal({
  dbModule,
  steps,
}: AddDownloadDataModalProps) {
  const tags = useStoreTags();
  const tagFormControl = React.useMemo(() => makeDownloadDataControl(), [tags]);
  const [error] = React.useState("");
  let manufacturer = "Brown University";
  let locationString = "ERROR";
  let sensorTop = "ERROR";
  let sensorBot = "ERROR";
  let sensorRotTop = "Not Rotated";
  let sensorRotBot = "Not Rotated";
  let FEHright = "ERROR";
  let FEHleft = "ERROR";
  let SEH = "ERROR";
  let VTRX = "ERROR";

  let mapsa = "ERROR";
  let mapsaRot = "Not Rotated";
  let baseplate = "ERROR";
  let POH = "ERROR";
  let ROH = "ERROR";
  let Carrier = "ERROR";
  const { location, serials } = dbModule;

  const status = (dbModule.tags ? dbModule.tags : []).some((tag) =>
    tags.includes(tag),
  )
    ? "Bad"
    : "Good";

  const incompleteString = (dbModule.tags ? dbModule.tags : []).some(
    (tag) => tag.toUpperCase() === "INCOMPLETE",
  )
    ? "Incomplete"
    : "Complete";

  const doSubmit = React.useCallback(async () => {
    if (steps) {
      for (const serial of serials) {
        const componentInfo = getPartTypeFromSerialNumber(serial);
        if (componentInfo.componentSubtype == "ROH") {
          ROH = componentInfo.serialNumber;
        } else if (componentInfo.componentSubtype == "POH") {
          POH = componentInfo.serialNumber;
        } else if (componentInfo.componentSubtype == "SEH") {
          SEH = componentInfo.serialNumber;
        } else if (componentInfo.componentSubtype == "FEH") {
          if (componentInfo.orientation == "Left") {
            FEHleft = componentInfo.serialNumber;
          } else if (componentInfo.orientation == "Right") {
            FEHright = componentInfo.serialNumber;
          }
        } else if (componentInfo.componentSubtype == "MaPSA") {
          mapsa = componentInfo.serialNumber;
        } else if (componentInfo.shorthand == "PS-s") {
          sensorTop = componentInfo.serialNumber;
        } else if (componentInfo.componentSubtype == "VTRX") {
          VTRX = componentInfo.serialNumber;
        } else if (componentInfo.componentType == "Baseplate") {
          baseplate = componentInfo.serialNumber;
        }
      }

      steps.forEach((step) => {
        const { stepName, details } = step;
        if (stepName.includes("2S_GLUE_SANDWICH")) {
          try {
            sensorTop = details.topSensorSerial as string;
            sensorBot = details.bottomSensorSerial as string;
          } catch (ex) {
            console.log("Could not get details");
          }
        }

        if (
          stepName.includes("2S_GLUE_SANDWICH") ||
          stepName.includes("SANDWICH_METROLOGY_TEST")
        ) {
          try {
            if (details.topOrientation) {
              sensorRotTop = details.topOrientation as string;
              sensorRotBot = details.bottomOrientation as string;
              if (sensorRotTop == "normal") {
                sensorRotTop = "Not Rotated";
              }
              if (sensorRotTop == "rotated") {
                sensorRotTop = "Rotated";
              }
              if (sensorRotBot == "normal") {
                sensorRotBot = "Not Rotated";
              }
              if (sensorRotBot == "rotated") {
                sensorRotBot = "Rotated";
              }
            }
          } catch (ex) {
            console.log("Could not get details");
          }
        }

        if (
          stepName.includes("PS_SENSOR_BASEPLATE_GLUING") ||
          stepName.includes("2S_GLUE_SANDWICH")
        ) {
          try {
            if (details.moduleCarrier) {
              Carrier = details.moduleCarrier as string;
            }
          } catch (ex) {
            console.log("Could not get details");
          }
        }

        if (stepName.includes("PS_TEST_SANDWICH_METROLOGY")) {
          try {
            if (details.topOrientation) {
              sensorRotTop = details.topOrientation as string;
              mapsaRot = details.bottomOrientation as string;
              if (sensorRotTop == "normal") {
                sensorRotTop = "Not Rotated";
              }
              if (sensorRotTop == "rotated") {
                sensorRotTop = "Rotated";
              }
              if (mapsaRot == "normal") {
                sensorRotBot = "Not Rotated";
              }
              if (mapsaRot == "rotated") {
                sensorRotBot = "Rotated";
              }
            }
          } catch (ex) {
            console.error("Could not get details");
          }
        }
      });
    }

    const locationName = location
      ? location.location
        ? location.location == "Courier"
          ? (location as ModuleLocationInTransit).origin
          : location.location
        : (location as unknown as string)
      : "";

    if (locationName.includes("Brown")) {
      manufacturer = "Brown University";
      locationString = "Brown";
    }
    if (locationName.includes("Rutgers")) {
      manufacturer = "Brown University";
      locationString = "US-Rutgers[Rutgers State Univ. of New Jersey]";
    }
    if (locationName.includes("Princeton")) {
      manufacturer = "Brown University";
      locationString = "US-Princeton[Princeton University]";
    }
    if (locationName.includes("Fermilab")) {
      manufacturer = "Fermilab";
      locationString = "US-Fermilab[Fermilab]";
    }

    let dbString =
      "NAME_LABEL," +
      dbModule.id +
      "," +
      status +
      "\n" +
      "MANUFACTURER," +
      manufacturer +
      "\n" +
      "LOCATION," +
      locationString;

    if (baseplate != "ERROR") {
      dbString = dbString + "\n" + "BASEPLATE," + baseplate;
    }

    if (sensorTop != "ERROR" && sensorBot != "ERROR") {
      dbString =
        dbString +
        "\n" +
        "SENSOR_TOP," +
        sensorTop +
        "," +
        sensorRotTop +
        "\n" +
        "SENSOR_BOTTOM," +
        sensorBot +
        "," +
        sensorRotBot;
    }

    if (mapsa != "ERROR" && sensorTop != "ERROR") {
      dbString =
        dbString +
        "\n" +
        "MAPSA," +
        mapsa +
        "," +
        mapsaRot +
        "\n" +
        "SENSOR_TOP," +
        sensorTop +
        "," +
        sensorRotTop;
    }

    if (FEHright != "ERROR") {
      dbString = dbString + "\n" + "FEH_RIGHT," + FEHright;
    }

    if (FEHleft != "ERROR") {
      dbString = dbString + "\n" + "FEH_LEFT," + FEHleft;
    }

    if (SEH != "ERROR") {
      dbString = dbString + "\n" + "SEH," + SEH;
    }

    if (ROH != "ERROR") {
      dbString = dbString + "\n" + "ROH," + ROH;
    }

    if (POH != "ERROR") {
      dbString = dbString + "\n" + "POH," + POH;
    }

    if (VTRX != "ERROR") {
      dbString = dbString + "\n" + "VTRX+," + VTRX;
    }
    if (Carrier != "ERROR") {
      dbString = dbString + "\n" + "CARRIER," + Carrier;
    }
    dbString = dbString + "\n" + "INCOMPLETE," + incompleteString;

    download(dbModule.id + ".csv", dbString);
  }, []);

  return (
    <ButtonModal label="Download Data" title="Download Data" size="sm">
      <JsonForm fields={tagFormControl} onSubmit={doSubmit} clearAfterSubmit />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
